<template>
<m-pageframe
    :title="title"
>
<v-container
    fluid
    style="padding: 15px"
>
    <p class="text-body-2 mb-4">
        Data cleaning rules help filter out invalid or test data from your integrations. Configure rules for different data types below.
    </p>

    <!-- Birthday Data Section -->
    <v-card class="mt-6 mb-6" outlined>
        <v-card-title class="grey lighten-4">
            <v-icon left color="primary">mdi-cake-variant</v-icon>
            Birthday Data
        </v-card-title>
        <v-card-text class="pt-4">
            <p class="text-body-2 grey--text text--darken-1 mb-4">
                Configure filters to exclude invalid birthday data from automated messaging features.
            </p>
                    <v-alert
                        v-if="!data_cleaning_rules || !data_cleaning_rules.is_certified"
                        type="warning"
                        dense
                        text
                        class="mb-4"
                    >
                        <strong>Birthday Messages Disabled:</strong> Birthday messages cannot not be sent until you certify your data cleaning rules.
                    </v-alert>

                    <!-- Built-in Filters Section -->
                    <div class="mb-6">
                        <h4 class="text-subtitle-1 mb-3">Built-in Filters</h4>
                        <p class="text-body-2 mb-3">
                            To ensure the accuracy of birthday data and automatically filter out potentially invalid entries, RedChirp applies the following non-configurable filters for automations:
                        </p>

                        <v-list dense>
                            <v-list-item>
                                <v-list-item-icon>
                                    <v-icon color="primary">mdi-check-circle</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>January 1st Birthdays</v-list-item-title>
                                    <v-list-item-subtitle>We filter out all birthdays on January 1st, as many CRM systems use this as a default placeholder date.</v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>

                            <v-list-item>
                                <v-list-item-icon>
                                    <v-icon color="primary">mdi-check-circle</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>Birthdays Over 100 Years Old</v-list-item-title>
                                    <v-list-item-subtitle>To remove unrealistic ages that may indicate test data, we automatically filter out birthdays older than 100 years.</v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>

                            <v-list-item>
                                <v-list-item-icon>
                                    <v-icon color="primary">mdi-check-circle</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>Invalid Placeholder Dates</v-list-item-title>
                                    <v-list-item-subtitle>We also filter out other known placeholder dates. Specifically, February 29, 1972 and July 16, 1969.</v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list>
                    </div>

                    <!-- Custom Ignored Dates Section -->
                    <div class="mb-6">
                        <h4 class="text-subtitle-1 mb-3">Custom Ignored Birth Dates</h4>
                        <p class="text-body-2 mb-3">
                            Add specific dates that should be excluded from automations using your birthday data (e.g., common test dates specific to your CRM):
                        </p>

                        <div class="d-flex align-center">
                            <v-combobox
                                label="Ignored Birth Dates"
                                v-model="display_ignored_birth_dates"
                                chips
                                multiple
                                hint="Enter dates in MM/DD/YYYY format (e.g., 12/25/2023 for December 25, 2023)"
                                persistent-hint
                                :rules="[validateBirthDateFormat]"
                                v-on:input="formatBirthDateInput"
                                v-on:change="updateIgnoredBirthDates"
                                class="flex-grow-1"
                                :disabled="saving"
                            >
                                <template v-slot:selection="{ attrs, item, select, selected }">
                                    <v-chip
                                        v-bind="attrs"
                                        :input-value="selected"
                                        close
                                        @click="select"
                                        @click:close="removeBirthDate(item)"
                                    >
                                        {{ Time.isoToMMDDYYYY(item) }}
                                    </v-chip>
                                </template>
                            </v-combobox>

                            <div class="ml-3">
                                <m-common-birthdays-dropdown
                                    :organization-id="$route.params.organization_id"
                                    :display-ignored-birth-dates="display_ignored_birth_dates"
                                    @add-ignored-date="addSingleIgnoredDate"
                                    @add-ignored-dates="addMultipleIgnoredDates"
                                    :disabled="saving"
                                />
                            </div>
                        </div>
                    </div>

        </v-card-text>
    </v-card>

            <!-- Future sections can be added here for other data types -->

    <!-- Global Certification Section -->
    <v-card class="mt-6 mb-6" outlined>
        <v-card-title class="grey lighten-4">
            <v-icon left color="primary">mdi-certificate</v-icon>
            Data Cleaning Certification
            <v-spacer></v-spacer>
            <v-chip
                :color="data_cleaning_rules && data_cleaning_rules.is_certified ? 'success' : 'warning'"
                text-color="white"
                small
            >
                {{ data_cleaning_rules && data_cleaning_rules.is_certified ? 'Certified' : 'Not Certified' }}
            </v-chip>
        </v-card-title>
        <v-card-text class="pt-4">
            <p class="text-body-2 grey--text text--darken-1 mb-4">
                You must certify that you have reviewed and approved all data cleaning rules above before automated features can be enabled.
            </p>

                    <!-- Certified State -->
                    <div v-if="data_cleaning_rules && data_cleaning_rules.is_certified">
                        <div v-if="last_certified_by || last_certified_time" class="mb-3">
                            <div v-if="last_certified_by" class="text-body-2 text--secondary">
                                Last certified by: {{ last_certified_by }}
                            </div>
                            <div v-if="last_certified_time" class="text-body-2 text--secondary">
                                Certified on: {{ formatCertificationTime(last_certified_time) }}
                            </div>
                        </div>

                        <v-btn
                            color="success"
                            disabled
                            outlined
                        >
                            <v-icon left>mdi-check-circle</v-icon>
                            Data Cleaning Rules Certified
                        </v-btn>
                    </div>

                    <!-- Not Certified State -->
                    <div v-else>
                        <v-btn
                            color="primary"
                            @click="showCertificationDialog"
                            :disabled="saving"
                            :loading="loading_certification_info"
                        >
                            <v-icon left>mdi-certificate</v-icon>
                            Certify Data Cleaning Rules
                        </v-btn>
                    </div>
        </v-card-text>
    </v-card>

</v-container>
</m-pageframe>
</template>

<script>
const Time = require('@/js/time.js').default;
const Validate = require('@/js/validate.js').default;

import MCommonBirthdaysDropdown from '@/components/auto-messages/m-common-birthdays-dropdown.vue';

export default {
    components: {
        MCommonBirthdaysDropdown
    },

    props: {
        profile: {
            type: Object,
            default: function() { return {}; }
        },
    },

    data() {
        return {
            data_cleaning_rules: null,
            display_ignored_birth_dates: [],
            saving: false,
            last_certified_by: null,
            last_certified_time: null,
            loading_certification_info: false,

            // Add Time and Validate to make them available in the template
            Time: Time,
            Validate: Validate,
        }
    },



    mounted() {
        this.loadDataCleaningRules();
        this.loadLastCertifiedBy();
    },

    computed: {
        title() {
            return "Data Cleaning Rules";
        },
    },

    methods: {
        loadDataCleaningRules() {
            this.$rc.rest({
                'endpoint': 'GET: /api/v1/organization-data-cleaning-rules/:organization_id',
                'params': {
                    'organization_id': this.$route.params.organization_id,
                },
                'on_success': (data) => {
                    this.data_cleaning_rules = data;

                    // Initialize display data
                    if (data.ignored_birth_dates && data.ignored_birth_dates.length > 0) {
                        this.display_ignored_birth_dates = this.convertDatesToDisplayFormat(data.ignored_birth_dates);
                    } else {
                        this.display_ignored_birth_dates = [];
                    }


                },
                'on_error': () => {
                    // If no rules exist yet, create default empty state
                    this.data_cleaning_rules = {
                        organization_id: this.$route.params.organization_id,
                        ignored_birth_dates: [],
                        is_certified: false
                    };
                    this.display_ignored_birth_dates = [];
                }
            });
        },

        autoSaveDataCleaningRules() {
            if (this.saving) {
                return; // Don't save if already saving
            }

            this.saving = true;

            const rules_data = {
                organization_id: this.$route.params.organization_id,
                ignored_birth_dates: this.transformDatesToBackendFormat(this.display_ignored_birth_dates),
                is_certified: this.data_cleaning_rules ? this.data_cleaning_rules.is_certified : false
            };

            this.$rc.rest({
                'endpoint': 'PUT: /api/v1/organization-data-cleaning-rules',
                'params': rules_data,
                'on_success': (data) => {
                    this.saving = false;
                    this.data_cleaning_rules = data;
                },
                'on_error': () => {
                    this.saving = false;
                    this.$rc.alert('Error', 'Unable to save data cleaning rules');
                }
            });
        },

        // Convert dates from backend format (YYYY-MM-DD) to display format (MM/DD/YYYY)
        convertDatesToDisplayFormat(backend_dates) {
            if (!backend_dates || !Array.isArray(backend_dates)) {
                return [];
            }
            
            return backend_dates.map(date => {
                // Backend stores as YYYY-MM-DD, convert to MM/DD/YYYY for display
                return Time.isoToMMDDYYYY(date);
            }).filter(date => date); // Filter out any invalid conversions
        },

        // Convert dates from display format (MM/DD/YYYY) to backend format (YYYY-MM-DD)
        transformDatesToBackendFormat(display_dates) {
            if (!display_dates || !Array.isArray(display_dates)) {
                return [];
            }
            
            return display_dates.map(date => {
                // Convert MM/DD/YYYY to YYYY-MM-DD for backend storage
                return Time.mmddyyyyToISOFormat(date);
            }).filter(date => date); // Filter out any invalid conversions
        },

        validateBirthDateFormat(value) {
            if (!value || value.length === 0) return true;
            
            // Validate each date in the array
            for (const date_str of value) {
                if (!this.Validate.isValidDateFormat(date_str)) {
                    return `Invalid date format: ${date_str}. Please use MM/DD/YYYY format.`;
                }
            }
            
            return true;
        },

        formatBirthDateInput() {
            // This method is called when the user types in the combobox
            // We'll let the validation handle the formatting
        },

        updateIgnoredBirthDates() {
            // Validate all birth dates before saving
            if (this.display_ignored_birth_dates && this.display_ignored_birth_dates.length > 0) {
                for (const date_str of [...this.display_ignored_birth_dates]) {
                    if (!this.Validate.isValidDateFormat(date_str)) {
                        this.$rc.alert('Validation Error', `Invalid date format: ${date_str}. All dates must be in MM/DD/YYYY or YYYY-MM-DD format.`);
                        this.display_ignored_birth_dates = this.display_ignored_birth_dates.filter(d => d !== date_str);
                    }
                }
            }

            // Any change to ignored birth dates invalidates certification
            this.invalidateCertification();
            this.autoSaveDataCleaningRules();
        },

        removeBirthDate(item) {
            const index = this.display_ignored_birth_dates.indexOf(item);
            if (index >= 0) {
                this.display_ignored_birth_dates.splice(index, 1);

                // Any change to ignored birth dates invalidates certification
                this.invalidateCertification();
                this.autoSaveDataCleaningRules();
            }
        },

        showCertificationDialog() {
            this.$rc.confirm({
                'title': 'Certify Data Cleaning Rules',
                'body': '<strong>Important:</strong> By certifying these data cleaning rules, you confirm that you have reviewed all settings and approve them for use in automated features.<br/><br/>Please confirm that you have:<br/>• Configured any custom ignored birth dates as needed<br/>• Understand that these rules will be applied to all automated features',
                'btn_text_ok': 'Certify Rules',
                'btn_text_cancel': 'Cancel',
                'on_ok': () => {
                    this.confirmCertification();
                }
            });
        },

        confirmCertification() {
            // Ensure data_cleaning_rules exists
            if (!this.data_cleaning_rules) {
                this.data_cleaning_rules = {
                    organization_id: this.$route.params.organization_id,
                    ignored_birth_dates: [],
                    is_certified: false
                };
            }

            // Set certification to true
            this.data_cleaning_rules.is_certified = true;

            // Save certification separately to ensure clear account log entry
            this.autoSaveDataCleaningRules();

            // Reload the last certified by info after certification
            setTimeout(() => {
                this.loadLastCertifiedBy();
            }, 3000);
        },

        loadLastCertifiedBy() {
            this.loading_certification_info = true;

            const search_params = {
                organization_id: this.$route.params.organization_id,
                type_object: 'organization-data-cleaning-rules',
                sort: 'time:desc'
            };

            this.$rc.rest({
                'endpoint': 'LIST: /api/v1/account-log',
                'params': search_params,
                'on_success': (data) => {
                    this.loading_certification_info = false;
                    if (data && data.results && data.results.length > 0) {
                        // Find the most recent certification change
                        for (const entry of data.results) {
                            if (entry.description && (entry.description.includes('is_certified') || entry.description.includes('Certified'))) {
                                this.last_certified_by = entry.by_employee_label || entry.by_employee_username || 'Unknown User';
                                this.last_certified_time = entry.time;
                                break;
                            }
                        }

                        // If no specific certification entry found, use the most recent entry
                        if (!this.last_certified_by && data.results.length > 0) {
                            const latest_entry = data.results[0];
                            this.last_certified_by = latest_entry.by_employee_label || latest_entry.by_employee_username || 'Unknown User';
                            this.last_certified_time = latest_entry.time;
                        }
                    }
                },
                'on_error': () => {
                    this.loading_certification_info = false;
                    // Silently fail - this is just for display purposes
                }
            });
        },

        formatCertificationTime(timestamp) {
            if (!timestamp) return '';

            // Convert timestamp to local browser timezone
            const date = new Date(timestamp);

            // Format as readable date and time
            return date.toLocaleString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                timeZoneName: 'short'
            });
        },

        // Common birthdays event handlers
        addSingleIgnoredDate(formatted_date) {
            if (!this.display_ignored_birth_dates.includes(formatted_date)) {
                this.display_ignored_birth_dates.push(formatted_date);

                // Any change to ignored birth dates invalidates certification
                this.invalidateCertification();
                this.autoSaveDataCleaningRules();
            }
        },

        addMultipleIgnoredDates(formatted_dates) {
            let added_count = 0;

            for (const formatted_date of formatted_dates) {
                if (!this.display_ignored_birth_dates.includes(formatted_date)) {
                    this.display_ignored_birth_dates.push(formatted_date);
                    added_count++;
                }
            }

            if (added_count > 0) {
                // Any change to ignored birth dates invalidates certification
                this.invalidateCertification();
                this.autoSaveDataCleaningRules();
            }
        },

        invalidateCertification() {
            // Any change to data cleaning rules invalidates certification
            if (this.data_cleaning_rules && this.data_cleaning_rules.is_certified) {
                this.data_cleaning_rules.is_certified = false;
            }
        },
    },
}
</script>

<style lang="scss" scoped>
</style>
